import { Gallery } from "@andrew-wolf/react-photoswipe-gallery";
import AiFiltersSelectableItem from "./AiFiltersSelectableItem";
import AIFiltersSelectableWrapper from "./AIFiltersSelectableWrapper";
import React, { useMemo, useState } from "react";
import {
  AiSelectableWrapperOptions,
  filterList,
  getAIFiltersPhotoSwipeCustomButtons,
} from "./aiFilter.helpers";
import type { Translationtype } from "utils";
import { AiFilterImageSelectorItem } from "./AiFilterImageSelectorItem";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { Alert } from "../alert";
import { AiFilterItemType } from "./types";

interface IAiFiltersImageSelectorProps {
  t: Translationtype;
  imagesList: any;
  handleSaveFilters: VoidFunction;
  handlePayFilters?: VoidFunction;
  selectedItems: string[];
  showLoader: boolean;
  selectedTab: string;
  setSelectedTab: React.Dispatch<React.SetStateAction<string>>;
  buttonLabel?: string;
  totalCost?: string;
  imagesToGenerate?: number;
  isButtonDisabled?: boolean;
  minImagesMessage?: string;
  locked?: boolean;
}

export function AiFiltersImageSelector({
  t,
  imagesList,
  handleSaveFilters,
  handlePayFilters,
  selectedItems,
  showLoader,
  selectedTab,
  setSelectedTab,
  buttonLabel,
  totalCost,
  imagesToGenerate,
  isButtonDisabled,
  minImagesMessage,
  locked = false,
}: IAiFiltersImageSelectorProps) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [paginatedItemsState, setPaginatedItemsState] = useState<
    AiFilterItemType[]
  >([]);
  const AiUIElements = useMemo(() => {
    return getAIFiltersPhotoSwipeCustomButtons(paginatedItemsState);
  }, [paginatedItemsState]);

  const AiOnBeforeOpen = (pswpInstance: any) => {
    // This code handles the back button on mobile (back button closes the modal)
    if (
      !history.state ||
      !history.state.pswp ||
      history.state.path !== window.location.pathname
    ) {
      history.pushState(
        { pswp: true, path: window.location.pathname },
        "",
        window.location.pathname
      );
    }
    window.addEventListener("popstate", () => {
      if (
        typeof pswpInstance === "object" &&
        pswpInstance &&
        pswpInstance.isOpen
      ) {
        history.pushState(
          { pswp: true, path: window.location.pathname },
          "",
          window.location.pathname
        );
        pswpInstance.close();
      } else {
        history.go(-1);
      }
    });

    pswpInstance.options.padding = { top: 0, bottom: 0, left: 0, right: 0 };
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  return (
    <AIFiltersSelectableWrapper
      items={imagesList}
      showLoader={showLoader}
      handleSaveFilters={() => handleSaveFilters()}
      handlePayFilters={handlePayFilters}
      buttonLabel={buttonLabel}
      totalCost={totalCost}
      imagesToGenerate={imagesToGenerate}
      isButtonDisabled={isButtonDisabled}
      minImagesMessage={minImagesMessage}
      locked={locked}
    >
      <TabContext value={selectedTab}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            width: "100%",
            display: "flex",
          }}
        >
          <TabList
            onChange={handleTabChange}
            sx={{
              width: "100%",
              "& .MuiTabs-flexContainer": {
                width: "100%",
                display: "flex",
              },
              "& .MuiTabs-scroller": {
                width: "100% !important",
              },
            }}
          >
            <Tab
              label={t("Global.magicAISelectedImages")}
              value="selectedFilters"
              sx={{
                flex: "1 1 50%",
                maxWidth: "none",
                fontWeight: 700,
              }}
            />
            <Tab
              label={t("Global.magicAISelectableImages")}
              value="filters"
              sx={{
                flex: "1 1 50%",
                maxWidth: "none",
                fontWeight: 700,
              }}
            />
          </TabList>
        </Box>
        <TabPanel value="selectedFilters">
          <Gallery
            options={AiSelectableWrapperOptions}
            onBeforeOpen={AiOnBeforeOpen}
            uiElements={AiUIElements}
            pagination={{
              items: filterList(imagesList, selectedItems),
              pageSize: imagesList?.length,
              paginationControl: "auto-by-scroll",
              displayItem: (paginatedItems: AiFilterItemType[]) => (
                <>
                  {selectedItems?.length === 0 ? (
                    <Alert
                      type="info"
                      className="max-w-2xl mx-auto"
                      message={t("Global.noAIFilterSelected")}
                    />
                  ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-6 3xl:grid-cols-10 gap-2 pswp-gallery">
                      {paginatedItems.map(
                        (item: AiFilterItemType, index: number) => {
                          return (
                            <AiFiltersSelectableItem
                              key={item.url}
                              id={item.name}
                              itemName={item.name}
                              isSelectable
                              locked={locked}
                            >
                              <AiFilterImageSelectorItem
                                item={item}
                                index={index}
                              />
                            </AiFiltersSelectableItem>
                          );
                        }
                      )}
                    </div>
                  )}
                </>
              ),
            }}
          />
        </TabPanel>
        <TabPanel value="filters">
          <Gallery
            options={AiSelectableWrapperOptions}
            onBeforeOpen={AiOnBeforeOpen}
            uiElements={AiUIElements}
            pagination={{
              items: filterList(imagesList, selectedItems, true),
              pageSize: imagesList?.length,
              paginationControl: "auto-by-scroll",
              displayItem: (paginatedItems: AiFilterItemType[]) => {
                const allSelected =
                  selectedItems?.length === imagesList?.length;
                return (
                  <>
                    {allSelected ? (
                      <Alert
                        type="info"
                        className="max-w-2xl mx-auto"
                        message={t("Global.allAIFilterSelected")}
                      />
                    ) : (
                      <div className="grid md:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-6 3xl:grid-cols-10 gap-2 pswp-gallery">
                        {paginatedItems.map(
                          (item: AiFilterItemType, index: number) => {
                            return (
                              <AiFiltersSelectableItem
                                key={item.url}
                                id={item.name}
                                itemName={item.name}
                                isSelectable
                                locked={locked}
                              >
                                <AiFilterImageSelectorItem
                                  item={item}
                                  index={index}
                                />
                              </AiFiltersSelectableItem>
                            );
                          }
                        )}
                      </div>
                    )}
                  </>
                );
              },
            }}
          />
        </TabPanel>
      </TabContext>
    </AIFiltersSelectableWrapper>
  );
}
